/*
 * Footer – Botanical Wellness theme
 * Real contact: 054-6362929 | Sharongol1971@gmail.com | רחוב שוהם, קדם
 * Includes: legal modals (accessibility + privacy), address with map icon
 */

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Instagram, Phone, Mail, ArrowUp, MapPin, X } from "lucide-react";

const LOGO = "https://d2xsxph8kpxj0f.cloudfront.net/310519663453381351/Su2TGRgRyunwHstmidEpSB/WhatsAppImage2026-03-22at11.32.24_87e6b455.jpeg";

type ModalType = "accessibility" | "privacy" | "terms" | null;

export default function Footer() {
  const [modal, setModal] = useState<ModalType>(null);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <>
      <footer className="bg-[#1B4332] text-white" style={{ direction: "rtl" }}>
        <div className="container py-12 md:py-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-16">
            {/* Brand */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img
                  src={LOGO}
                  alt="שרון גולדברג לוגו"
                  className="w-12 h-12 rounded-full object-cover border-2 border-[#E8A87C]/40"
                />
                <span
                  className="text-xl font-bold"
                  style={{ fontFamily: "var(--font-heading)" }}
                >
                  שרון גולדברג
                </span>
              </div>
              <p
                className="text-[#95B8A3] leading-relaxed text-sm"
                style={{ fontFamily: "var(--font-body)" }}
              >
                מאמנת כושר ובריאות עם למעלה מ-30 שנות ניסיון.
                <br />
                בוגרת מכון וינגייט. מתמחה בכושר לגיל השלישי,
                מחלימות מסרטן, פילאטיס ואימון פונקציונלי.
              </p>
            </div>

            {/* Quick links */}
            <div>
              <h3
                className="text-lg font-bold mb-4"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                ניווט מהיר
              </h3>
              <nav className="flex flex-col gap-2">
                {[
                  { href: "#hero", label: "ראשי" },
                  { href: "#about", label: "אודות" },
                  { href: "#specializations", label: "התמחויות" },
                  { href: "#services", label: "אימונים" },
                  { href: "#gallery", label: "גלריה" },
                  { href: "#contact", label: "צרי קשר" },
                ].map((link) => (
                  <button
                    key={link.href}
                    onClick={() =>
                      document.querySelector(link.href)?.scrollIntoView({ behavior: "smooth" })
                    }
                    className="text-[#95B8A3] hover:text-[#E8A87C] transition-colors text-sm text-right"
                    style={{ fontFamily: "var(--font-body)" }}
                  >
                    {link.label}
                  </button>
                ))}
              </nav>
            </div>

            {/* Contact & Social */}
            <div>
              <h3
                className="text-lg font-bold mb-4"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                יצירת קשר
              </h3>
              <div className="flex flex-col gap-3">
                <a
                  href="https://www.instagram.com/sharon1fit/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-[#95B8A3] hover:text-[#E8A87C] transition-colors text-sm"
                  style={{ fontFamily: "var(--font-body)" }}
                >
                  <Instagram className="w-4 h-4 flex-shrink-0" />
                  @Sharon1fit
                </a>
                <a
                  href="tel:+9720546362929"
                  className="flex items-center gap-2 text-[#95B8A3] hover:text-[#E8A87C] transition-colors text-sm"
                  style={{ fontFamily: "var(--font-body)" }}
                >
                  <Phone className="w-4 h-4 flex-shrink-0" />
                  <span dir="ltr">054-6362929</span>
                </a>
                <a
                  href="mailto:Sharongol1971@gmail.com"
                  className="flex items-center gap-2 text-[#95B8A3] hover:text-[#E8A87C] transition-colors text-sm break-all"
                  style={{ fontFamily: "var(--font-body)" }}
                >
                  <Mail className="w-4 h-4 flex-shrink-0" />
                  Sharongol1971@gmail.com
                </a>
                <a
                  href="https://maps.google.com/?q=רחוב+שוהם,+קדם,+ישראל"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-[#95B8A3] hover:text-[#E8A87C] transition-colors text-sm"
                  style={{ fontFamily: "var(--font-body)" }}
                >
                  <MapPin className="w-4 h-4 flex-shrink-0" />
                  רחוב שוהם, קדם, ישראל
                </a>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-white/10 mt-10 pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-6">
                <button
                  onClick={() => setModal("accessibility")}
                  className="text-[#95B8A3] hover:text-[#E8A87C] text-xs transition-colors underline underline-offset-2"
                  style={{ fontFamily: "var(--font-body)" }}
                >
                  הצהרת נגישות
                </button>
                <a
                  href="/privacy"
                  className="text-[#95B8A3] hover:text-[#E8A87C] text-xs transition-colors underline underline-offset-2"
                  style={{ fontFamily: "var(--font-body)" }}
                >
                  מדיניות פרטיות
                </a>
                <button
                  onClick={() => setModal("terms")}
                  className="text-[#95B8A3] hover:text-[#E8A87C] text-xs transition-colors underline underline-offset-2"
                  style={{ fontFamily: "var(--font-body)" }}
                >
                  תנאי שימוש
                </button>
              </div>

              <p
                className="text-[#95B8A3] text-xs"
                style={{ fontFamily: "var(--font-body)" }}
              >
                &copy; {new Date().getFullYear()} שרון גולדברג – כל הזכויות שמורות
              </p>

              {/* Back to top */}
              <button
                onClick={scrollToTop}
                className="w-10 h-10 bg-white/10 hover:bg-[#E8A87C] rounded-full flex items-center justify-center transition-all duration-300"
                aria-label="חזרה למעלה"
              >
                <ArrowUp className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* Legal Modals */}
      <AnimatePresence>
        {modal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => setModal(null)}
            style={{ direction: "rtl" }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto p-8"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <button
                onClick={() => setModal(null)}
                className="absolute top-4 left-4 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="סגור"
              >
                <X className="w-5 h-5" />
              </button>

              {modal === "accessibility" && (
                <>
                  <h2
                    className="text-2xl font-bold text-[#1B4332] mb-4"
                    style={{ fontFamily: "var(--font-heading)" }}
                  >
                    הצהרת נגישות
                  </h2>
                  <div
                    className="text-[#3D5A4A] text-sm leading-relaxed space-y-3"
                    style={{ fontFamily: "var(--font-body)" }}
                  >
                    <p>
                      אתר זה של שרון גולדברג – מאמנת כושר ובריאות – נבנה תוך מחויבות מלאה לנגישות דיגיטלית, בהתאם לתקן הישראלי <strong>ת"י 5568</strong> ולהנחיות WCAG 2.1 ברמה AA.
                    </p>
                    <p><strong>הנגישות שלנו כוללת:</strong></p>
                    <ul className="list-disc list-inside space-y-1 mr-2">
                      <li>תמיכה מלאה בקוראי מסך (NVDA, JAWS, VoiceOver)</li>
                      <li>ניווט מלא באמצעות מקלדת בלבד</li>
                      <li>גופנים גדולים וניגודיות גבוהה לנוחות קריאה</li>
                      <li>תמיכה בהגדלת טקסט עד 200% ללא אובדן תוכן</li>
                      <li>תגיות alt לכל התמונות</li>
                      <li>מבנה כותרות היררכי ברור</li>
                      <li>אתר מותאם במיוחד לגיל השלישי ולמשתמשים עם מוגבלויות</li>
                    </ul>
                    <p>
                      <strong>פנייה בנושא נגישות:</strong><br />
                      אם נתקלתם בבעיית נגישות, אנא פנו אלינו:
                    </p>
                    <p>
                      📧 <a href="mailto:Sharongol1971@gmail.com" className="text-[#2D5A3D] underline">Sharongol1971@gmail.com</a><br />
                      📞 <a href="tel:+9720546362929" className="text-[#2D5A3D] underline" dir="ltr">054-6362929</a>
                    </p>
                    <p className="text-xs text-gray-400 mt-4">הצהרה זו עודכנה לאחרונה: מרץ 2026</p>
                  </div>
                </>
              )}

              {modal === "privacy" && (
                <>
                  <h2
                    className="text-2xl font-bold text-[#1B4332] mb-4"
                    style={{ fontFamily: "var(--font-heading)" }}
                  >
                    מדיניות פרטיות
                  </h2>
                  <div
                    className="text-[#3D5A4A] text-sm leading-relaxed space-y-3"
                    style={{ fontFamily: "var(--font-body)" }}
                  >
                    <p>שרון גולדברג מכבדת את פרטיות המשתמשים באתר ומחויבת להגן על המידע האישי שלכם.</p>
                    <p>
                      <strong>איסוף מידע:</strong><br />
                      האתר אינו אוסף מידע אישי ללא הסכמתכם. פנייה דרך WhatsApp, טלפון או אימייל מהווה הסכמה לקבלת מענה בחזרה.
                    </p>
                    <p>
                      <strong>שימוש במידע:</strong><br />
                      המידע שתמסרו ישמש אך ורק לצורך תיאום אימונים ומתן שירות אישי. לא יועבר לצדדים שלישיים.
                    </p>
                    <p>
                      <strong>קישורים חיצוניים:</strong><br />
                      האתר מכיל קישורים לאינסטגרם ו-WhatsApp. מדיניות הפרטיות של פלטפורמות אלו חלה על שימושכם בהן.
                    </p>
                    <p>
                      <strong>עוגיות (Cookies):</strong><br />
                      האתר משתמש בעוגיות אנליטיקה בסיסיות לשיפור חוויית המשתמש. אין שימוש בעוגיות פרסומיות.
                    </p>
                    <p>
                      <strong>זכות למחיקת מידע:</strong><br />
                      לבקשת מחיקת מידע אישי, פנו אלינו: <a href="mailto:Sharongol1971@gmail.com" className="text-[#2D5A3D] underline">Sharongol1971@gmail.com</a>
                    </p>
                    <p className="text-xs text-gray-400 mt-4">מדיניות זו עודכנה לאחרונה: מרץ 2026</p>
                  </div>
                </>
              )}

              {modal === "terms" && (
                <>
                  <h2
                    className="text-2xl font-bold text-[#1B4332] mb-4"
                    style={{ fontFamily: "var(--font-heading)" }}
                  >
                    תנאי שימוש
                  </h2>
                  <div
                    className="text-[#3D5A4A] text-sm leading-relaxed space-y-3"
                    style={{ fontFamily: "var(--font-body)" }}
                  >
                    <p>
                      <strong>תנאי השימוש באתר זה:</strong>
                    </p>
                    <p>
                      השימוש באתר של שרון גולדברג כפוף לתנאים הבאים. על ידי הגלישה באתר, אתה מסכים לתנאים אלו.
                    </p>
                    <p>
                      <strong>1. שימוש חוקי:</strong><br />
                      האתר מיועד לשימוש חוקי בלבד. אסור להשתמש באתר למטרות אלימות, הטרדה, או כל פעילות בלתי חוקית.
                    </p>
                    <p>
                      <strong>2. זכויות יוצרים:</strong><br />
                      כל התוכן באתר (טקסט, תמונות, לוגו) הוא קניין רוחני של שרון גולדברג. אסור להעתיק או להשתמש בתוכן ללא אישור מפורש.
                    </p>
                    <p>
                      <strong>3. אחריות:</strong><br />
                      האתר מסופק "כמו שהוא". שרון גולדברג אינה אחראית לנזקים כלשהם הנובעים מהשימוש או אי-השימוש באתר.
                    </p>
                    <p>
                      <strong>4. שינויים:</strong><br />
                      שרון גולדברג שומרת לעצמה את הזכות לשנות או להסיר תוכן מהאתר בכל עת ללא הודעה מוקדמת.
                    </p>
                    <p className="text-xs text-gray-400 mt-4">תנאים אלו עודכנו לאחרונה: מרץ 2026</p>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
